import type { ThunkAction } from "redux-thunk";
import type { Cart, CartItem } from "@/types/cart";
import type { RootState } from "@/store/store";

export type { Cart, CartItem };

const actionTypes = {
  SET_FULL_CART: "SET_FULL_CART",
  ADD_NEW_CART_ITEM: "ADD_NEW_CART_ITEM",
  UPDATE_CART_ITEM: "UPDATE_CART_ITEM",
  CLEAR_CART_ITEMS: "CLEAR_CART_ITEMS",
  REMOVE_CART_ERROR: "REMOVE_CART_ERROR",
} as const;

type CartAction =
  | { type: typeof actionTypes.SET_FULL_CART; payload: Cart }
  | { type: typeof actionTypes.ADD_NEW_CART_ITEM; payload: CartItem }
  | { type: typeof actionTypes.UPDATE_CART_ITEM; payload: { key: string; item: CartItem } }
  | { type: typeof actionTypes.CLEAR_CART_ITEMS }
  | { type: typeof actionTypes.REMOVE_CART_ERROR; payload: number };

const initState: Cart | null = null;

// action creators
const actions = {
  setFullCart: (cart: Cart) => ({
    type: actionTypes.SET_FULL_CART,
    payload: cart,
  }),

  addNewCartItem: (cartItem: CartItem) => ({
    type: actionTypes.ADD_NEW_CART_ITEM,
    payload: cartItem,
  }),

  updateCartItem: (key: string, item: CartItem) => ({
    type: actionTypes.UPDATE_CART_ITEM,
    payload: { key, item },
  }),

  clearCartItems: () => ({
    type: actionTypes.CLEAR_CART_ITEMS,
  }),

  removeCartError: (errorIndex: number) => ({
    type: actionTypes.REMOVE_CART_ERROR,
    payload: errorIndex,
  }),
};

// Thunk action creators
const thunkActions = {
  addCartItem: (newItem: CartItem): ThunkAction<void, RootState, undefined, CartAction> => {
    return (dispatch, getState) => {
      try {
        const cartState = getState().cart;

        if (!cartState) {
          console.warn("購物車尚未初始化，無法加入商品");
          return;
        }

        // 判斷商品是否已存在（使用 key 作為唯一識別）
        const existingItem = cartState.items.find((item) => item.key === newItem.key);

        if (existingItem) {
          // 商品已存在，更新商品資訊（API 回傳的 newItem 已包含正確的數量）
          dispatch(actions.updateCartItem(newItem.key, newItem));
        } else {
          dispatch(actions.addNewCartItem(newItem));
        }
      } catch (error) {
        console.error("加入購物車商品時發生錯誤:", error);
        // Optimize: handle error helper
      }
    };
  },
};

// reducer
const reducer = (state: Cart | null = initState, action: CartAction): Cart | null => {
  switch (action.type) {
    case actionTypes.SET_FULL_CART: {
      return action.payload;
    }

    case actionTypes.ADD_NEW_CART_ITEM: {
      if (!state) return state;

      return {
        ...state,
        items: [...state.items, action.payload],
      };
    }

    case actionTypes.UPDATE_CART_ITEM: {
      if (!state) return state;

      const { key, item: newItem } = action.payload;
      const itemIndex = state.items.findIndex((item) => item.key === key);

      if (itemIndex === -1) {
        return state;
      }

      const newItems = [...state.items];
      newItems[itemIndex] = newItem;

      return {
        ...state,
        items: newItems,
      };
    }

    case actionTypes.CLEAR_CART_ITEMS: {
      if (!state) return state;
      return { ...state, items: [] };
    }

    case actionTypes.REMOVE_CART_ERROR: {
      if (!state) return state;
      const remainingErrors = state.errors.filter((_, index) => index !== action.payload);
      return { ...state, errors: remainingErrors };
    }

    default:
      return state;
  }
};

export { reducer, actions, thunkActions };
